import BaseController from "./BaseController";
import Fragment from "sap/ui/core/Fragment";
import JSONModel from "sap/ui/model/json/JSONModel";
import MessageBox from "sap/m/MessageBox";
import MessageToast from "sap/m/MessageToast";
import type Dialog from "sap/m/Dialog";
import type Table from "sap/m/Table";
import type ColumnListItem from "sap/m/ColumnListItem";
import type Context from "sap/ui/model/odata/v4/Context";
import type ODataListBinding from "sap/ui/model/odata/v4/ODataListBinding";
import type { SearchField$LiveChangeEvent, SearchField$SearchEvent } from "sap/m/SearchField";
import type { ListBase$SelectionChangeEvent } from "sap/m/ListBase";
import type { ResponseError } from "../types/Errors";
import { getCountries, type CountryItem } from "../model/countries";
import { problemMessage } from "../util/ProblemDetails";

type OrgType = "Partner" | "EndCustomer";
type IssuerMode = "CompuTec" | "Partner";

interface IssuerPickerState {
	searchQuery: string;
	selectedId: string;
	selectedName: string;
	selectedPath: string;
}

/** Te same pola co w OrganizationDetail / ProfileEditDraft + name/taxId (które tam są na entity). */
interface OrgNewDraft {
	name: string;
	taxId: string;
	country: string;
	city: string;
	region: string;
	postalCode: string;
	email: string;
	contractDate: string;
	contractExpiration: string;
	customerStatus: string;
	cardCode: string;
}

interface OrgNewState {
	type: OrgType;
	title: string;
	parentDisplay: string;
	parentOrgId: string;
	issuerMode: IssuerMode;
	issuerOrgId: string;
	issuerPath: string;
	issuerDisplay: string;
	cardCodeValueState: "None" | "Error";
	countries: CountryItem[];
	draft: OrgNewDraft;
}

const UUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
const CARD_CODE_PATTERN = /^[A-Za-z0-9]{1,50}$/;

const EMPTY_DRAFT: OrgNewDraft = {
	name: "",
	taxId: "",
	country: "",
	city: "",
	region: "",
	postalCode: "",
	email: "",
	contractDate: "",
	contractExpiration: "",
	customerStatus: "",
	cardCode: ""
};

/**
 * Strona tworzenia partnera / klienta końcowego (route: <c>partners/new</c> lub
 * <c>customers/new</c>). Layout taki sam jak <c>OrganizationDetail</c> w trybie edycji
 * (ObjectPageLayout + ObjectPageSection/SubSection + SimpleForm) — user widzi tę samą
 * "stronę" czy zakłada nową org czy edytuje istniejącą.
 *
 * Save chain (atomicity best-effort, nie tx):
 *  1. POST /api/v1/organizations { parent, type, name, taxId } — zwraca id nowej org.
 *  2. Jeśli wypełnione którekolwiek pole profilu (adres / kontrakt / customer status) →
 *     PUT /api/v1/organizations/{id}/profile.
 *  3. Jeśli wypełniony cardCode i user ma orgs.edit-integration →
 *     PUT /api/v1/organizations/{id}/integration.
 *  4. NavTo organizationDetail z nowym id.
 *
 * Reguły parenta — patrz <see cref="resolveImplicitParent"/>.
 *
 * @namespace computec.b2b.portal.controller
 */
export default class OrganizationNew extends BaseController {
	private issuerPickerDialog?: Dialog;
	private computecOrgId?: string;

	public override onInit(): void {
		const router = this.getRouter();
		router.getRoute("partnerNew")?.attachPatternMatched(() => { void this.applyType("Partner"); });
		router.getRoute("customerNew")?.attachPatternMatched(() => { void this.applyType("EndCustomer"); });
	}

	private async applyType(type: OrgType): Promise<void> {
		const bundle = await this.getResourceBundle();
		const initial: OrgNewState = {
			type,
			title: bundle.getText(type === "Partner" ? "organizations.new.partner.title" : "organizations.new.customer.title")
				?? (type === "Partner" ? "New partner" : "New customer"),
			parentDisplay: "",
			parentOrgId: "",
			issuerMode: "CompuTec",
			issuerOrgId: "",
			issuerPath: "",
			issuerDisplay: "",
			cardCodeValueState: "None",
			countries: getCountries(),
			draft: { ...EMPTY_DRAFT }
		};
		this.getView().setModel(new JSONModel(initial), "wiz");
		this.getView().setModel(new JSONModel({ searchQuery: "", selectedId: "", selectedName: "", selectedPath: "" } satisfies IssuerPickerState), "issuerPicker");

		await this.resolveImplicitParent();
	}

	/**
	 * Ustala parent na bazie roli usera + typu tworzonej org. Async — CompuTec singleton fetchowany
	 * z OData. Wynik trafia w wiz>/parentOrgId + wiz>/parentDisplay (label dla usera).
	 *  - Type=Partner → parent zawsze CompuTec (tylko CompuTec admin tworzy partnerów).
	 *  - Type=EndCustomer + CompuTec admin → tryb wystawcy decyduje (CompuTec direct = singleton; Partner X = X).
	 *  - Type=EndCustomer + partner-admin → parent = własna organizacja (user>/orgId).
	 */
	private async resolveImplicitParent(): Promise<void> {
		const wiz = this.getStateModel();
		const state = wiz.getData() as OrgNewState;
		const isCompuTec = this.hasIssuerChoice();

		if (state.type === "Partner") {
			const id = await this.ensureComputecOrgId();
			wiz.setProperty("/parentOrgId", id ?? "");
			wiz.setProperty("/parentDisplay", id ? "CompuTec" : "");
			return;
		}

		if (isCompuTec) {
			const id = await this.ensureComputecOrgId();
			wiz.setProperty("/parentOrgId", id ?? "");
			wiz.setProperty("/parentDisplay", id ? "CompuTec" : "");
			return;
		}

		const userModel = this.getOwnerComponent().getModel("user") as JSONModel | undefined;
		const ownOrgId = (userModel?.getProperty("/orgId") as string | undefined) ?? "";
		const ownOrgName = (userModel?.getProperty("/orgName") as string | undefined) ?? "";
		wiz.setProperty("/parentOrgId", ownOrgId);
		wiz.setProperty("/parentDisplay", ownOrgName.length > 0 ? ownOrgName : "—");
	}

	private hasIssuerChoice(): boolean {
		return this.getOwnerComponent().getPermissions().has("orgs.read-all");
	}

	private getStateModel(): JSONModel {
		return this.getView().getModel("wiz") as JSONModel;
	}

	public override onNavBack(): void {
		const wiz = this.getStateModel().getData() as OrgNewState;
		this.navTo(wiz.type === "Partner" ? "partners" : "customers", {});
	}

	public onCardCodeLiveChange(): void {
		const wiz = this.getStateModel();
		const value = (wiz.getProperty("/draft/cardCode") as string).trim();
		const ok = value.length === 0 || CARD_CODE_PATTERN.test(value);
		wiz.setProperty("/cardCodeValueState", ok ? "None" : "Error");
	}

	// === Issuer mode (CompuTec admin creating EndCustomer) ===

	public onIssuerModeChange(): void {
		const wiz = this.getStateModel();
		wiz.setProperty("/issuerOrgId", "");
		wiz.setProperty("/issuerPath", "");
		wiz.setProperty("/issuerDisplay", "");
		const mode = wiz.getProperty("/issuerMode") as IssuerMode;
		if (mode === "CompuTec") {
			wiz.setProperty("/parentOrgId", this.computecOrgId ?? "");
			wiz.setProperty("/parentDisplay", this.computecOrgId ? "CompuTec" : "");
		} else {
			wiz.setProperty("/parentOrgId", "");
			wiz.setProperty("/parentDisplay", "");
		}
	}

	public async onIssuerValueHelp(): Promise<void> {
		if (!this.issuerPickerDialog) {
			this.issuerPickerDialog = await Fragment.load({
				id: this.getView().getId(),
				name: "computec.b2b.portal.view.IssuerPickerDialog",
				controller: this
			}) as Dialog;
			this.getView().addDependent(this.issuerPickerDialog);
			this.issuerPickerDialog.attachAfterOpen(() => this.applyIssuerPickerFilters());
		}

		const wiz = this.getStateModel().getData() as OrgNewState;
		const pickerModel = this.getView().getModel("issuerPicker") as JSONModel;
		pickerModel.setProperty("/searchQuery", "");
		pickerModel.setProperty("/selectedId", wiz.issuerOrgId);
		pickerModel.setProperty("/selectedName", wiz.issuerDisplay);
		pickerModel.setProperty("/selectedPath", wiz.issuerPath);

		this.applyIssuerPickerFilters();
		this.issuerPickerDialog.open();
	}

	public onIssuerPickerSearch(event: SearchField$SearchEvent | SearchField$LiveChangeEvent): void {
		const params = event.getParameters() as { query?: string; newValue?: string };
		const raw = params.query ?? params.newValue ?? "";
		(this.getView().getModel("issuerPicker") as JSONModel).setProperty("/searchQuery", raw);
		this.applyIssuerPickerFilters();
	}

	public onIssuerPickerSelectionChange(event: ListBase$SelectionChangeEvent): void {
		const item = event.getParameter("listItem") as ColumnListItem | undefined;
		const ctx = item?.getBindingContext("odata") as Context | null | undefined;
		const pickerModel = this.getView().getModel("issuerPicker") as JSONModel;
		if (!ctx) {
			pickerModel.setProperty("/selectedId", "");
			pickerModel.setProperty("/selectedName", "");
			pickerModel.setProperty("/selectedPath", "");
			return;
		}
		pickerModel.setProperty("/selectedId", String(ctx.getProperty("Id") ?? ""));
		pickerModel.setProperty("/selectedName", String(ctx.getProperty("Name") ?? ""));
		pickerModel.setProperty("/selectedPath", String(ctx.getProperty("Path") ?? ""));
	}

	public onIssuerPickerConfirm(): void {
		const picker = (this.getView().getModel("issuerPicker") as JSONModel).getData() as IssuerPickerState;
		if (!UUID_RE.test(picker.selectedId)) {
			return;
		}
		const wiz = this.getStateModel();
		wiz.setProperty("/issuerOrgId", picker.selectedId);
		wiz.setProperty("/issuerPath", picker.selectedPath);
		wiz.setProperty("/issuerDisplay", picker.selectedName);
		wiz.setProperty("/parentOrgId", picker.selectedId);
		wiz.setProperty("/parentDisplay", picker.selectedName);

		this.issuerPickerDialog?.close();
	}

	public onIssuerPickerCancel(): void {
		this.issuerPickerDialog?.close();
	}

	private applyIssuerPickerFilters(): void {
		const dialog = this.issuerPickerDialog;
		if (!dialog) {
			return;
		}
		const table = this.byId("issuerPickerTable") as Table | undefined;
		const binding = table?.getBinding("items") as ODataListBinding | undefined;
		if (!binding) {
			return;
		}
		const state = (this.getView().getModel("issuerPicker") as JSONModel).getData() as IssuerPickerState;
		const parts: string[] = ["Type eq 'Partner'"];
		if (state.searchQuery.trim().length > 0) {
			const qLower = escapeODataString(state.searchQuery.trim().toLowerCase());
			parts.push(`contains(tolower(Name),'${qLower}')`);
		}
		binding.changeParameters({ $filter: parts.join(" and ") });
	}

	private async ensureComputecOrgId(): Promise<string | undefined> {
		if (this.computecOrgId) {
			return this.computecOrgId;
		}
		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			const r = await fetcher.fetch(
				"/odata/v4/Organizations?$filter=Type%20eq%20'CompuTec'&$select=Id&$top=1",
				{ headers: { Accept: "application/json" } }
			);
			if (!r.ok) {
				return undefined;
			}
			const data = await r.json() as { value?: { Id: string }[] };
			this.computecOrgId = data.value?.[0]?.Id;
			return this.computecOrgId;
		} catch {
			return undefined;
		}
	}

	// === Submit ===

	public async onSubmit(): Promise<void> {
		const bundle = await this.getResourceBundle();
		const wiz = this.getStateModel();
		const state = wiz.getData() as OrgNewState;
		const draft = state.draft;

		const errors: string[] = [];
		if (this.hasIssuerChoice() && state.type === "EndCustomer" && state.issuerMode === "Partner" && !UUID_RE.test(state.issuerOrgId)) {
			errors.push(bundle.getText("organizations.new.issuer.error.partnerRequired") ?? "Pick the partner");
		}
		if (draft.name.trim().length === 0) {
			errors.push(bundle.getText("organizations.new.error.nameRequired") ?? "Name is required");
		}
		if (!UUID_RE.test(state.parentOrgId)) {
			errors.push(bundle.getText("organizations.new.error.parentMissing") ?? "Could not resolve parent organization");
		}
		const cardCode = draft.cardCode.trim();
		if (cardCode.length > 0 && !CARD_CODE_PATTERN.test(cardCode)) {
			wiz.setProperty("/cardCodeValueState", "Error");
			errors.push(bundle.getText("organization.detail.cardCode.invalidFormat") ?? "CardCode invalid");
		}
		if (errors.length > 0) {
			MessageBox.warning(errors.join("\n"));
			return;
		}

		const fetcher = this.getOwnerComponent().getAuthFetch();
		try {
			// 1. POST create
			const created = await fetcher.json<{ id: string } | string>("/api/v1/organizations", {
				method: "POST",
				body: JSON.stringify({
					parentOrganizationId: state.parentOrgId,
					type: state.type,
					name: draft.name.trim(),
					taxId: draft.taxId.trim().length > 0 ? draft.taxId.trim() : null
				})
			});
			// Endpoint zwraca Result<Guid> — w zależności od konfiguracji może to być raw guid
			// jako string albo object { id }. Akceptujemy obie formy.
			const newId = typeof created === "string" ? created : created.id;
			if (!UUID_RE.test(newId)) {
				throw new Error("Backend did not return new organization id");
			}

			// 2. PUT profile (jeśli choć jedno pole profilu wypełnione)
			if (this.hasAnyProfileField(draft)) {
				await fetcher.json(`/api/v1/organizations/${newId}/profile`, {
					method: "PUT",
					body: JSON.stringify(this.buildProfilePayload(draft))
				});
			}

			// 3. PUT integration (tylko gdy perm + cardCode wypełniony)
			const permissions = this.getOwnerComponent().getPermissions();
			if (cardCode.length > 0 && permissions.has("orgs.edit-integration")) {
				await fetcher.json(`/api/v1/organizations/${newId}/integration`, {
					method: "PUT",
					body: JSON.stringify({ cardCode })
				});
			}

			const successKey = state.type === "Partner"
				? "organizations.new.success.partner"
				: "organizations.new.success.customer";
			MessageToast.show(bundle.getText(successKey) ?? "Organization created");
			this.getOwnerComponent().getModel("odata")?.refresh();
			this.navToOrganization(newId);
		} catch (e) {
			const err = e as ResponseError;
			MessageBox.error(problemMessage(err.bodyText, bundle.getText("organizations.new.failure") ?? "Failed to create organization"));
		}
	}

	private hasAnyProfileField(draft: OrgNewDraft): boolean {
		return [
			draft.country, draft.city, draft.region, draft.postalCode, draft.email,
			draft.contractDate, draft.contractExpiration, draft.customerStatus
		].some(v => v.trim().length > 0);
	}

	private buildProfilePayload(draft: OrgNewDraft): Record<string, string | null> {
		const trim = (v: string): string | null => {
			const t = v.trim();
			return t.length > 0 ? t : null;
		};
		return {
			country: trim(draft.country),
			city: trim(draft.city),
			region: trim(draft.region),
			postalCode: trim(draft.postalCode),
			email: trim(draft.email),
			contractDate: trim(draft.contractDate),
			contractExpirationDate: trim(draft.contractExpiration),
			customerStatus: trim(draft.customerStatus)
		};
	}

}

/** Escape a single quote per OData v4 string literal rules: ' → '' */
function escapeODataString(value: string): string {
	return value.replace(/'/g, "''");
}
